import { AlertTriangle } from 'lucide-react';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Message } from '@/Domain/api/Conversation/dto/Message.dto';
import { WhyAISaidThatDisplay } from '@/Domain/components/Inbox/Message/WhyAISaidThatDisplay';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/Domain/components/Shared/Popover/Popover';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import { useWhyAISaidThat } from '@/Domain/hooks/Conversation/useWhyAISaidThat/useWhyAISaidThat.hook';

type Props = {
  message: Message;
  conversationId: number;
  children: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  disableElevation?: boolean;
};

export const WhyAISaidThatPopover = ({
  message,
  conversationId,
  children,
  open: controlledOpen,
  onOpenChange,
  disableElevation = false,
}: Props) => {
  const { t } = useTranslation('inbox_v2');
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const { capture } = usePostHogAnalytics();
  const hasTrackedOpen = useRef(false);

  // Support both controlled and uncontrolled modes
  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : uncontrolledOpen;

  const setIsOpen = (newOpen: boolean) => {
    if (!isControlled) {
      setUncontrolledOpen(newOpen);
    }
    onOpenChange?.(newOpen);
  };

  const {
    whyAISaidThat,
    isLoading,
    isFetched,
    isScheduled,
    isScheduling,
    scheduleGeneration,
    resetScheduled,
  } = useWhyAISaidThat(conversationId, isOpen ? message.id : undefined);

  // Auto-schedule generation when data is not available
  useEffect(() => {
    if (isOpen && isFetched && !whyAISaidThat && !isScheduled && !isScheduling) {
      scheduleGeneration();
    }
  }, [isOpen, isFetched, whyAISaidThat, isScheduled, isScheduling, scheduleGeneration]);

  // Reset scheduled state when popover closes
  useEffect(() => {
    if (!isOpen && isScheduled) {
      resetScheduled();
    }
  }, [isOpen, isScheduled, resetScheduled]);

  // Track PostHog event when popover opens (after we know if data was available)
  useEffect(() => {
    if (isOpen && isFetched && !hasTrackedOpen.current) {
      capture(PostHogEventEnum.WhyAiSaidThatOpened, {
        message_id: message.id,
        conversation_id: conversationId,
        data_immediately_available: !!whyAISaidThat,
      });
      hasTrackedOpen.current = true;
    }
  }, [isOpen, isFetched, whyAISaidThat, capture, message.id, conversationId]);

  const showLoading = isLoading || isScheduling;
  const showScheduled = !showLoading && !whyAISaidThat && isScheduled;
  const showData = !showLoading && whyAISaidThat;

  return (
    <>
      {/* Backdrop overlay when popover is open - only render from the main trigger, not button */}
      {isOpen && !disableElevation && (
        <div
          className="fixed inset-0 bg-black/20 backdrop-blur-[1px] z-40 transition-opacity"
          onClick={() => setIsOpen(false)}
          data-testid="why-ai-said-that-backdrop"
        />
      )}

      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          {/* Wrapper div that elevates the message above the blur overlay when open */}
          <div
            className={
              isOpen && !disableElevation
                ? 'relative z-[45] rounded-xl transition-all duration-200 ease-out'
                : 'transition-all duration-200 ease-out'
            }
          >
            {children}
          </div>
        </PopoverTrigger>
        <PopoverContent
          side="left"
          align="center"
          sideOffset={16}
          className="w-[460px] max-h-[85vh] overflow-hidden p-0 rounded-xl border-0 shadow-md bg-card z-50"
          data-testid="why-ai-said-that-popover"
        >
          {showLoading ? (
            <div
              className="flex items-center justify-center py-16"
              data-testid="why-ai-said-that-loading"
            >
              <Spinner className="h-7 w-7 text-primary-500" />
            </div>
          ) : showData ? (
            <div
              className="overflow-y-auto max-h-[85vh] p-6"
              data-testid="why-ai-said-that-content"
            >
              <WhyAISaidThatDisplay whyAISaidThat={whyAISaidThat} />
            </div>
          ) : showScheduled ? (
            <div
              className="flex flex-col items-center justify-center py-12 text-center gap-4 px-8"
              data-testid="why-ai-said-that-scheduled"
            >
              <Spinner className="h-7 w-7 text-primary-500" />
              <div className="flex flex-col gap-2">
                <p className="text-sm font-medium text-muted-foreground">
                  {t('why_ai_said_that_popover.analysis_scheduled')}
                </p>
                <p className="text-xs text-foreground-subtle leading-relaxed">
                  {t('why_ai_said_that_popover.analysis_scheduled_description')}
                </p>
              </div>
            </div>
          ) : (
            <div
              className="flex flex-col items-center justify-center py-12 text-center gap-4 px-8"
              data-testid="why-ai-said-that-error"
            >
              <div className="p-3 rounded-full bg-muted">
                <AlertTriangle className="text-foreground-subtle size-7" />
              </div>
              <div className="flex flex-col gap-2">
                <p className="text-sm font-medium text-muted-foreground">
                  {t('why_ai_said_that_popover.unable_to_generate')}
                </p>
                <p className="text-xs text-foreground-subtle leading-relaxed">
                  {t('why_ai_said_that_popover.contact_support')}{' '}
                  <a
                    href="mailto:support@quickchat.ai"
                    className="text-primary-600 hover:text-primary-700 font-medium"
                    data-testid="why-ai-said-that-support-email"
                  >
                    support@quickchat.ai
                  </a>
                </p>
              </div>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </>
  );
};
